// src/utils/exportPedidosXlsx.js
import { collection, getDocs } from "firebase/firestore";

/**
 * Pool simple para limitar concurrencia (buenas prácticas)
 */
async function mapLimit(items, limit, fn) {
  const res = new Array(items.length);
  let i = 0;

  async function worker() {
    while (i < items.length) {
      const idx = i++;
      res[idx] = await fn(items[idx], idx);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
  await Promise.all(workers);
  return res;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export async function fetchGastosDePedido(db, pedidoId) {
  const listasSnap = await getDocs(
    collection(db, `pedidos/${pedidoId}/listasGastos`),
  );

  let total = 0;
  const rows = [];

  for (const listaDoc of listasSnap.docs) {
    const lista = listaDoc.data() || {};
    const listaId = listaDoc.id;
    const listaNombre = lista.nombre || "";

    const prodsSnap = await getDocs(
      collection(
        db,
        `pedidos/${pedidoId}/listasGastos/${listaId}/productosGastos`,
      ),
    );

    for (const prodDoc of prodsSnap.docs) {
      const p = prodDoc.data() || {};
      const cantidad = safeNum(p.cantidad);
      const precioUnitario = safeNum(p.precioUnitario);
      const importe = cantidad * precioUnitario;

      total += importe;

      rows.push({
        pedidoId,
        listaId,
        listaNombre,
        gastoId: prodDoc.id,
        descripcion: p.descripcion || "",
        noParte: p.noParte || "",
        cantidad,
        precioUnitario,
        importe,
      });
    }
  }

  return { total, rows };
}

/**
 * Exporta XLSX (lazy import para no inflar bundle)
 */
export async function exportXlsx({ filename, sheets }) {
  const [{ utils, write }, fileSaverMod] = await Promise.all([
    import("xlsx"),
    import("file-saver"),
  ]);

  // ✅ compat: según bundler puede venir como:
  // fileSaverMod.saveAs
  // fileSaverMod.default.saveAs
  // fileSaverMod.default (si default ES la función)
  const saveAs =
    fileSaverMod?.saveAs ||
    fileSaverMod?.default?.saveAs ||
    fileSaverMod?.default;

  if (typeof saveAs !== "function") {
    throw new Error(
      "file-saver no expuso la función saveAs. Revisa la instalación/import.",
    );
  }

  const wb = utils.book_new();

  sheets.forEach(({ name, rows }) => {
    const ws = utils.json_to_sheet(rows || []);
    utils.book_append_sheet(wb, ws, name);
  });

  const data = write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, filename);
}

/**
 * 1) Exporta SOLO pedidos (tabla)
 */
export function buildPedidosRows(orders) {
  return orders.map((o) => ({
    Fecha: o.fechaOrden || "",
    "No. Orden": o.numeroOrdenDeCompra || "",
    Cliente: o.nombreCliente || "",
    Descripcion: o.referencia || "",
    Estado: o.estado || "",
    Divisa: o.divisa || "",
    Importe: safeNum(o.total),
    Vendedor: o.vendedorNombre || "",
    Division: o.division || "",
    Planta: o.planta || "",
    Area: o.area || "",
  }));
}

/**
 * 2) Exporta Pedidos + Total gastos + Balance (ingreso - gastos)
 */
export async function buildPedidosConGastosRows(
  db,
  orders,
  { concurrency = 6, onProgress } = {},
) {
  const results = await mapLimit(orders, concurrency, async (o, idx) => {
    const { total: totalGastos } = await fetchGastosDePedido(db, o.id);
    onProgress?.(idx + 1, orders.length);

    const ingreso = safeNum(o.total);
    const balance = ingreso - totalGastos;

    return {
      Fecha: o.fechaOrden || "",
      "No. Orden": o.numeroOrdenDeCompra || "",
      Cliente: o.nombreCliente || "",
      Descripcion: o.referencia || "",
      Estado: o.estado || "",
      Divisa: o.divisa || "",
      Ingreso: ingreso,
      "Total Gastos": totalGastos,
      Balance: balance,
      PedidoId: o.id,
    };
  });

  return results;
}

/**
 * 3) Exporta DETALLE de gastos (cada fila)
 */
export async function buildDetalleGastosRows(
  db,
  orders,
  { concurrency = 4, onProgress } = {},
) {
  const all = [];
  let done = 0;

  // menos concurrencia porque cada pedido abre varias subcolecciones
  const perOrder = await mapLimit(orders, concurrency, async (o) => {
    const { rows } = await fetchGastosDePedido(db, o.id);
    done += 1;
    onProgress?.(done, orders.length);

    // añade info del pedido a cada fila
    return rows.map((r) => ({
      PedidoId: o.id,
      "No. Orden": o.numeroOrdenDeCompra || "",
      Cliente: o.nombreCliente || "",
      Fecha: o.fechaOrden || "",
      Estado: o.estado || "",
      Divisa: o.divisa || "",
      Lista: r.listaNombre,
      Descripcion: r.descripcion,
      NoParte: r.noParte,
      Cantidad: r.cantidad,
      PrecioUnitario: r.precioUnitario,
      Importe: r.importe,
    }));
  });

  perOrder.forEach((arr) => all.push(...arr));
  return all;
}
